/**
 * Con qué marca se pinta el panel (CLAUDE.md §36). El mismo panel sirve a Wixy (`panel.wixy.app`) y a
 * cada agencia revendedora en su host (`<agencia>.wixy.app` o su dominio propio): el núcleo dice cuál
 * es por el host (`GET /api/publico/marca`), y se pregunta **antes de pintar nada**, para que en el
 * panel de una agencia no asome ni un instante el nombre ni el icono de Wixy.
 *
 * Por los hosts de Wixy la marca es `null` y todo sigue como siempre. Una agencia sin logo sale con su
 * nombre y su inicial; sin color, con uno neutro: nunca con los de Wixy.
 */
export type Marca = {
  nombre: string
  activo: boolean
  logo: null | string
  logoOscuro: null | string
  favicon: null | string
  colorPrincipal: null | string
  lema: null | string
  soporte: { correo: null | string; url: null | string; whatsapp: null | string }
  direccion: string
  /** Si quien llega a este panel puede crearse una cuenta solo (R6). */
  registro?: boolean
  /** El dominio bajo el que salen las webs de sus clientes, si ya está comprobado (R6). */
  dominioWebs?: null | string
}

/** Un color sobrio para la agencia que no ha elegido el suyo. */
const NEUTRO = '#334155'

let actual: Marca | null = null

/** La marca de este panel: `null` en los de Wixy. */
export const marca = (): Marca | null => actual
/** Si este panel es el de una agencia: sin socios, sin nada que diga Wixy. */
export const esDeAgencia = (): boolean => actual !== null
/** El nombre que firma el panel. */
export const nombreDeLaMarca = (): string => actual?.nombre || 'Wixy'

/** Los hosts que son de Wixy aunque el núcleo no conteste: si la pregunta falla en otro, mejor neutro que «Wixy». */
const esHostDeWixy = (host: string) => /^(panel|equipo)\./.test(host) || host === 'localhost' || host === '127.0.0.1'

const inicial = (nombre: string) => (nombre.trim()[0] ?? '·').toUpperCase()

/** Un icono de pestaña con la inicial de la agencia sobre su color: lo que sale si no ha subido el suyo. */
const iconoConInicial = (nombre: string, color: string) =>
  `data:image/svg+xml,${encodeURIComponent(`<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 32 32"><rect width="32" height="32" rx="7" fill="${color}"/><text x="16" y="22" font-family="system-ui,sans-serif" font-size="18" font-weight="700" fill="#fff" text-anchor="middle">${inicial(nombre).replace(/[<&"]/g, '')}</text></svg>`)}`

const aplicar = () => {
  document.title = nombreDeLaMarca()
  let icono = document.querySelector<HTMLLinkElement>('link[rel="icon"]')
  if (!icono) {
    icono = document.createElement('link')
    icono.rel = 'icon'
    document.head.appendChild(icono)
  }
  if (!actual) {
    icono.type = 'image/svg+xml'
    icono.href = '/favicon.svg'
    return
  }
  const color = /^#[0-9a-f]{6}$/i.test(actual.colorPrincipal ?? '') ? actual.colorPrincipal! : NEUTRO
  icono.removeAttribute('type')
  icono.href = actual.favicon ?? iconoConInicial(actual.nombre, color)
  // El acento del panel pasa a ser el de la agencia, en claro y en oscuro (sobre el papel de cada modo).
  const raiz = document.documentElement.style
  raiz.setProperty('--color-violeta', color)
  raiz.setProperty('--color-violeta-2', `color-mix(in oklch, ${color} 82%, black)`)
  raiz.setProperty('--color-violeta-suave', `color-mix(in oklch, ${color} 12%, var(--color-papel))`)
  raiz.setProperty('--color-violeta-borde', `color-mix(in oklch, ${color} 35%, var(--color-papel))`)
  raiz.setProperty('--shadow-foco', `0 0 0 3px color-mix(in oklch, ${color} 22%, transparent)`)
  raiz.setProperty('--gradiente-marca', `linear-gradient(135deg, ${color} 0%, color-mix(in oklch, ${color} 55%, black) 100%)`)
}

/** Se llama una vez, antes de pintar. Nunca falla: si el núcleo no contesta, decide el host. */
export const cargarMarca = async (): Promise<void> => {
  try {
    const r = await fetch('/api/publico/marca', { credentials: 'same-origin', headers: { accept: 'application/json' } })
    if (!r.ok) throw new Error(String(r.status))
    actual = ((await r.json()) as { marca: Marca | null }).marca ?? null
  } catch {
    actual = esHostDeWixy(location.hostname) ? null : { nombre: 'Panel', activo: true, logo: null, logoOscuro: null, favicon: null, colorPrincipal: null, lema: null, soporte: { correo: null, url: null, whatsapp: null }, direccion: location.origin }
  }
  aplicar()
}

/** El logo del panel: el de la agencia si lo tiene (el oscuro en modo oscuro), o la inicial y el nombre. */
export const LogoDeMarca = ({ tamano = 'normal', conNombre = true }: { tamano?: 'grande' | 'normal'; conNombre?: boolean }) => {
  const m = actual
  const nombre = nombreDeLaMarca()
  const caja = tamano === 'grande' ? 'size-8' : 'size-7'
  if (m?.logo) {
    return (
      <span className="flex items-center gap-2.5">
        <img alt={nombre} className={`${tamano === 'grande' ? 'h-8' : 'h-7'} w-auto max-w-40 object-contain ${m.logoOscuro ? 'dark:hidden' : ''}`} src={m.logo} />
        {m.logoOscuro && <img alt={nombre} className={`${tamano === 'grande' ? 'h-8' : 'h-7'} w-auto max-w-40 object-contain hidden dark:block`} src={m.logoOscuro} />}
      </span>
    )
  }
  return (
    <span className="flex items-center gap-2.5">
      <span aria-hidden className={`flex ${caja} items-center justify-center rounded-lg text-[13px] font-bold text-white`} style={{ background: 'var(--gradiente-marca)' }}>
        {m ? inicial(nombre) : 'W'}
      </span>
      {conNombre && <span className={`font-display ${tamano === 'grande' ? 'text-lg' : 'text-[17px]'} font-semibold tracking-tight`}>{nombre}</span>}
    </span>
  )
}
